import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Alert, Box, Button, LinearProgress, Stack } from "@mui/material";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { downloadAuthenticatedFile } from "network/download-authenticated-file";
import { leagueCustomerMatchSheetPreviewRoute } from "network/api-routes";

export const PREVIEW_DEBOUNCE_MS = 350;
// The PDF is A4 landscape (297 x 210 mm); at 96 dpi that is 1123 x 794 CSS px.
const PAGE_WIDTH = 1123;
const PAGE_HEIGHT = 794;

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data)?.message || fallback;
    } catch (parseError) {
      return fallback;
    }
  }
  return data?.message || fallback;
};

/**
 * Live preview of the match-sheet PDF. The HTML comes from the backend, rendered with the same template as the
 * real PDF from example data and the (possibly unsaved) `columns`. It is shown in a sandboxed iframe.
 */
const MatchSheetPreview = ({ customerId, columns }) => {
  const intl = useIntl();
  const { user } = useContext(UserContext);
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [scale, setScale] = useState(1);
  const frameRef = useRef(null);
  const requestId = useRef(0);

  const columnsKey = JSON.stringify(columns);

  useEffect(() => {
    if (!customerId) return undefined;
    const body = { match_sheet: { columns: JSON.parse(columnsKey) } };
    const timer = setTimeout(() => {
      const current = ++requestId.current;
      setLoading(true);
      performRequest("POST", leagueCustomerMatchSheetPreviewRoute(customerId), body, true, user, null, {}, false, "text")
        .then((response) => {
          if (current !== requestId.current) return;
          setHtml(typeof response.data === "string" ? response.data : String(response.data ?? ""));
          setError(null);
        })
        .catch((requestError) => {
          if (current !== requestId.current) return;
          setError(errorMessage(requestError, intl.formatMessage({ id: "gestion.settings.matchSheet.preview.error" })));
        })
        .finally(() => {
          if (current === requestId.current) setLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      // Columns changed (or unmounting): a response still in flight for the old columns must not show.
      requestId.current += 1;
    };
  }, [customerId, columnsKey, user, intl]);

  useEffect(
    () => () => {
      requestId.current += 1;
    },
    [],
  );

  useLayoutEffect(() => {
    const element = frameRef.current;
    if (!element) return undefined;
    const update = () => setScale(Math.min(1, (element.clientWidth || PAGE_WIDTH) / PAGE_WIDTH));
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleDownload = useCallback(() => {
    if (!customerId) return;
    setDownloading(true);
    downloadAuthenticatedFile({
      method: "POST",
      url: `${leagueCustomerMatchSheetPreviewRoute(customerId)}?format=pdf`,
      params: { match_sheet: { columns } },
      user,
      filename: "planilla-ejemplo.pdf",
      accept: "application/pdf",
    })
      .catch(() => setError(intl.formatMessage({ id: "gestion.settings.matchSheet.preview.error" })))
      .finally(() => setDownloading(false));
  }, [columns, customerId, intl, user]);

  return (
    <Stack spacing={1.5} data-testid="match-sheet-preview">
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Box>
          <RevaText variant="subtitle1">{intl.formatMessage({ id: "gestion.settings.matchSheet.preview.title" })}</RevaText>
          <RevaText variant="body2" color="text.secondary">
            {intl.formatMessage({ id: "gestion.settings.matchSheet.preview.hint" })}
          </RevaText>
        </Box>
        <Button variant="outlined" onClick={handleDownload} disabled={downloading || !customerId}>
          {intl.formatMessage({ id: "gestion.settings.matchSheet.preview.download" })}
        </Button>
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Box sx={{ height: 4 }}>
        {loading ? (
          <LinearProgress
            aria-label={intl.formatMessage({ id: "gestion.settings.matchSheet.preview.loading" })}
            data-testid="match-sheet-preview-loading"
          />
        ) : null}
      </Box>
      <Box
        ref={frameRef}
        sx={{
          width: "100%",
          height: PAGE_HEIGHT * scale,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "common.white",
          opacity: loading ? 0.7 : 1,
          transition: "opacity 150ms",
        }}
      >
        {html !== null ? (
          <iframe
            title={intl.formatMessage({ id: "gestion.settings.matchSheet.preview.iframeTitle" })}
            sandbox=""
            srcDoc={html}
            style={{
              width: PAGE_WIDTH,
              height: PAGE_HEIGHT,
              border: 0,
              background: "#fff",
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          />
        ) : null}
      </Box>
    </Stack>
  );
};

export default MatchSheetPreview;
